import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ClipboardCheck, PhoneCall } from 'lucide-react';
import { useBedCountConfig } from '@/hooks/useBedCountConfig';
import { useDailyWaitlist } from '@/hooks/useDailyWaitlist';
import { logActivity } from '@/hooks/useActivityLog';
import type { BedCount } from '@/hooks/useBedCount';

interface AvcReportCardProps {
  bedCount: BedCount;
  siteId: string;
  /** When true, hide editable controls (used for print). */
  printMode?: boolean;
}

const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * AVC Report — auto-generated from a completed Bed Count.
 * 8 line items + overage details + Mark as Called In.
 */
export const AvcReportCard = ({ bedCount, siteId, printMode = false }: AvcReportCardProps) => {
  const { data: cfgData } = useBedCountConfig(siteId);
  const cfg = cfgData?.config;
  const siteName = cfgData?.siteName;

  const [offline, setOffline] = useState<number>(bedCount.offline_beds ?? 0);
  const [emergencies, setEmergencies] = useState<number>(bedCount.reported_emergencies ?? 0);
  const [savingMeta, setSavingMeta] = useState(false);
  const [callingIn, setCallingIn] = useState(false);

  useEffect(() => {
    setOffline(bedCount.offline_beds ?? cfg?.offline_beds ?? 0);
    setEmergencies(bedCount.reported_emergencies ?? 0);
  }, [bedCount.id, bedCount.offline_beds, bedCount.reported_emergencies, cfg?.offline_beds]);

  // Bedded census at this site (right now) — Total Census line item
  const { data: census = 0 } = useQuery({
    queryKey: ['avc-census', siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('clients')
        .select('id', { count: 'exact', head: true })
        .eq('site_id', siteId)
        .is('archived_at', null)
        .not('room_id', 'is', null);
      if (error) throw error;
      return count ?? 0;
    },
  });

  // Active passes for pass-related line items (now with explicit pass_type)
  const { data: passes = [] } = useQuery({
    queryKey: ['avc-passes', siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('clients')
        .select('id, pass_start, pass_end, pass_type')
        .eq('site_id', siteId)
        .is('archived_at', null)
        .not('pass_start', 'is', null)
        .not('pass_end', 'is', null)
        .lte('pass_start', nowIso)
        .gte('pass_end', nowIso);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Active reserved beds today
  const { data: reservedCount = 0 } = useQuery({
    queryKey: ['avc-reserved', siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const today = new Date();
      const ymd = today.toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('reserved_beds')
        .select('id, room_id, reservation_start, reservation_end, status, rooms!inner(site_id)')
        .eq('status', 'active')
        .lte('reservation_start', ymd)
        .gte('reservation_end', ymd);
      if (error) throw error;
      return (data ?? []).filter((r: any) => r.rooms?.site_id === siteId).length;
    },
  });

  // Waitlist for today (overages)
  const today = new Date().toISOString().slice(0, 10);
  const { data: waitlist = [] } = useDailyWaitlist(siteId, today);
  const overages = waitlist.filter((w) => w.reason === 'overage');

  // Resolve clients for overage table
  const overageClientIds = overages.map((o) => o.client_id).filter(Boolean) as string[];
  const { data: overageClients = [] } = useQuery({
    queryKey: ['avc-overage-clients', overageClientIds.sort().join(',')],
    enabled: overageClientIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, first_name, last_name')
        .in('id', overageClientIds);
      if (error) throw error;
      return data ?? [];
    },
  });
  const clientById = useMemo(() => {
    const m = new Map<string, any>();
    overageClients.forEach((c: any) => m.set(c.id, c));
    return m;
  }, [overageClients]);

  // Bed-count operational night window (8pm previous day -> 8am following day, anchored to scheduled_for)
  const { windowStart, windowEnd } = useMemo(() => {
    const sched = new Date(bedCount.scheduled_for);
    const start = new Date(sched); start.setHours(20, 0, 0, 0);
    if (sched.getHours() < 12) start.setDate(start.getDate() - 1);
    const end = new Date(start); end.setDate(end.getDate() + 1); end.setHours(8, 0, 0, 0);
    return { windowStart: start.toISOString(), windowEnd: end.toISOString() };
  }, [bedCount.scheduled_for]);

  // Auto-count emergency notes for this site within the count window
  const { data: emergencyNotes = [] } = useQuery({
    queryKey: ['avc-emergency-notes', siteId, windowStart, windowEnd],
    enabled: !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notes')
        .select('id, title, created_by, created_at, client_id')
        .eq('site_id', siteId)
        .eq('category', 'emergency')
        .gte('created_at', windowStart)
        .lte('created_at', windowEnd);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Pass categorization — explicit pass_type wins; fall back to date heuristic for legacy rows
  const now = new Date();
  const endOfToday = new Date(); endOfToday.setHours(23, 59, 59, 999);
  const typed = (t: 'late' | 'overnight' | 'multi_use') =>
    passes.filter((p: any) => p.pass_type === t).length;
  const legacyPasses = passes.filter((p: any) => !p.pass_type);
  const legacyLate = legacyPasses.filter((p: any) => new Date(p.pass_end) <= endOfToday).length;
  const legacyOvernight = legacyPasses.filter((p: any) => {
    const end = new Date(p.pass_end);
    const days = (end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return days > 0 && days <= 1.5;
  }).length;
  const legacyMulti = legacyPasses.filter((p: any) => {
    const end = new Date(p.pass_end);
    const days = (end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return days > 1.5;
  }).length;
  const latePasses = typed('late') + legacyLate;
  const overnightPasses = typed('overnight') + legacyOvernight;
  const multiUsePasses = typed('multi_use') + legacyMulti;

  // Names for "Completed By" / "Called in by"
  const userIds = [bedCount.signed_off_by, bedCount.called_in_by].filter(Boolean) as string[];
  const { data: userNames = {} } = useQuery({
    queryKey: ['avc-users', userIds.sort().join(',')],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);
      if (error) throw error;
      const m: Record<string, string> = {};
      (data ?? []).forEach((p: any) => { m[p.id] = p.full_name || 'Unknown'; });
      return m;
    },
  });

  const completedBy = bedCount.signed_off_by ? (userNames[bedCount.signed_off_by] || '—') : '—';
  const calledInBy = bedCount.called_in_by ? (userNames[bedCount.called_in_by] || '—') : null;

  const scheduled = new Date(bedCount.scheduled_for);
  const calledInAt = bedCount.called_in_at ? new Date(bedCount.called_in_at) : null;

  const saveMeta = async () => {
    setSavingMeta(true);
    const { error } = await supabase
      .from('bed_counts')
      .update({ offline_beds: offline, reported_emergencies: emergencies })
      .eq('id', bedCount.id);
    setSavingMeta(false);
    if (error) {
      toast.error('Failed to save AVC values');
      return;
    }
    toast.success('AVC values saved');
  };

  const markCalledIn = async () => {
    setCallingIn(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setCallingIn(false); return; }
    const { error } = await supabase
      .from('bed_counts')
      .update({ called_in_at: new Date().toISOString(), called_in_by: user.id })
      .eq('id', bedCount.id);
    if (error) {
      setCallingIn(false);
      toast.error(error.message || 'Failed to mark as called in');
      return;
    }
    await logActivity({
      actionType: 'bed_count_avc_call' as any,
      actionDetails: { event: 'bed_count_avc_call', bed_count_id: bedCount.id },
      siteId,
    });
    setCallingIn(false);
    toast.success('AVC report marked as called in');
  };

  const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular-nums">{value}</span>
    </div>
  );

  return (
    <Card className="avc-report">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardCheck className="h-4 w-4" /> AVC Report
            </CardTitle>
            <div className="text-sm text-muted-foreground mt-1">
              {siteName ?? 'Facility'}
              {cfg?.facility_code && <> · <span className="font-mono">{cfg.facility_code}</span></>}
              {' · '}
              {scheduled.toLocaleDateString()} · {fmtTime(scheduled)}
            </div>
          </div>
          {!printMode && (
            calledInAt ? (
              <Badge variant="secondary" className="gap-1">
                <PhoneCall className="h-3 w-3" />
                Called in {fmtTime(calledInAt)}{calledInBy && ` · ${calledInBy}`}
              </Badge>
            ) : (
              <Button size="sm" onClick={markCalledIn} disabled={callingIn} className="gap-2">
                <PhoneCall className="h-4 w-4" />
                {callingIn ? 'Saving…' : 'Mark as Called In'}
              </Button>
            )
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-x-6">
          <div>
            <Row label="Total Census" value={census} />
            <Row
              label="Offline Beds"
              value={
                printMode ? (
                  offline
                ) : (
                  <Input
                    type="number"
                    min={0}
                    value={offline}
                    onChange={(e) => setOffline(Number(e.target.value) || 0)}
                    className="h-7 w-20 text-right"
                  />
                )
              }
            />
            <Row label="Late Passes" value={latePasses} />
            <Row label="Overnight Passes" value={overnightPasses} />
          </div>
          <div>
            <Row label="Multi-use Passes" value={multiUsePasses} />
            <Row
              label="Reported Emergencies"
              value={
                printMode ? (
                  bedCount.reported_emergencies ?? emergencyNotes.length
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="tabular-nums">
                      {bedCount.reported_emergencies != null && bedCount.reported_emergencies > 0
                        ? bedCount.reported_emergencies
                        : emergencyNotes.length}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {bedCount.reported_emergencies != null && bedCount.reported_emergencies > 0
                        ? '(override)'
                        : '(auto)'}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      placeholder="Override"
                      value={emergencies || ''}
                      onChange={(e) => setEmergencies(Number(e.target.value) || 0)}
                      className="h-7 w-20 text-right"
                    />
                  </div>
                )
              }
            />
            <Row label="Reserved Beds" value={reservedCount} />
            <Row label="Overages" value={overages.length} />
          </div>
        </div>

        {emergencyNotes.length > 0 && !printMode && (
          <div className="text-xs text-muted-foreground">
            Auto-counted from {emergencyNotes.length} emergency note{emergencyNotes.length === 1 ? '' : 's'} created during this count window.
          </div>
        )}

        {overages.length > 0 && (
          <div>
            <Label className="text-xs text-muted-foreground">Overage Details</Label>
            <div className="mt-1 border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2 font-medium">Client Name</th>
                    <th className="text-left p-2 font-medium w-32">Arrival</th>
                    <th className="text-left p-2 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {overages.map((o) => {
                    const c = o.client_id ? clientById.get(o.client_id) : null;
                    const name = c
                      ? `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()
                      : o.arrival_name;
                    return (
                      <tr key={o.id} className="border-t">
                        <td className="p-2">{name || '—'}</td>
                        <td className="p-2">{fmtTime(new Date(o.arrival_time))}</td>
                        <td className="p-2 text-muted-foreground">{o.notes ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
          <span>Completed By: <span className="font-medium text-foreground">{completedBy}</span></span>
          {!printMode && (
            <Button variant="outline" size="sm" onClick={saveMeta} disabled={savingMeta}>
              {savingMeta ? 'Saving…' : 'Save AVC Values'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
